<template>
    <div class="profile-page">
        <div class="page-header">
            <h1>{{ $t('menu.user_profile.page.header') }}</h1>
            <p class="page-subtitle">{{ $t('menu.user_profile.page.subtitle') }}</p>
        </div>

        <div class="profile-grid">
            <!-- Profile card -->
            <div class="tap-card profile-card">
                <div class="profile-card-header">
                    <div class="profile-avatar">
                        {{ userInitials }}
                    </div>
                    <div class="profile-identity">
                        <div class="profile-name">{{ user.userName }}</div>
                        <div class="profile-email">{{ user.userEmail }}</div>
                    </div>
                </div>

                <div class="profile-details">
                    <div class="profile-field">
                        <div class="field-label">{{ $t('menu.user_profile.page.userDepartment') }}</div>
                        <div class="field-value">
                            <span class="tap-badge tap-badge-accent">{{ user.userDepartment }}</span>
                        </div>
                    </div>
                    <div class="profile-field">
                        <div class="field-label">{{ $t('menu.user_profile.page.userRole') }}</div>
                        <div class="field-value">{{ user.userRole }}</div>
                    </div>
                </div>
            </div>

            <!-- Markets card -->
            <div class="tap-card markets-card">
                <h3>{{ $t('menu.user_profile.page.userTranslationMarkets') }}</h3>
                <div class="markets-list">
                    <template v-for="(lang, index) in Object.keys(user.translations.userLangs)" :key="index">
                        <div v-if="user.translations.userLangs[lang].visible" class="market-item">
                            <div class="market-dot"></div>
                            <span>{{ user.translations.userLangs[lang].shopifyLong }}</span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Change password card -->
            <div class="tap-card password-card">
                <h3>{{ $t('menu.user_profile.page.changePassword') }}</h3>
                <div class="password-form">
                    <div class="password-field">
                        <label>{{ $t('menu.user_profile.page.currentPassword') }}</label>
                        <input type="password" v-model="passwordForm.current" autocomplete="current-password" />
                    </div>
                    <div class="password-field">
                        <label>{{ $t('menu.user_profile.page.newPassword') }}</label>
                        <input type="password" v-model="passwordForm.next" autocomplete="new-password" />
                    </div>
                    <div class="password-field">
                        <label>{{ $t('menu.user_profile.page.confirmPassword') }}</label>
                        <input type="password" v-model="passwordForm.confirm" autocomplete="new-password" />
                    </div>
                    <div v-if="passwordFeedback" :class="['password-feedback', passwordSuccess ? 'ok' : 'err']">
                        {{ passwordFeedback }}
                    </div>
                    <button
                        class="appButton"
                        :disabled="passwordSaving || !passwordForm.current || !passwordForm.next"
                        @click="submitPasswordChange"
                    >
                        {{ $t('menu.user_profile.page.savePassword') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useSettings } from '@/store/user'
import mixins from '@/components/mixins';
import { computed } from 'vue';

export default {
    mixins: [mixins],
    setup() {
        const user = useSettings();

        const userInitials = computed(() => {
            if (!user.userName) return '?';
            return user.userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        });

        return { user, userInitials }
    },
    data() {
        return {
            passwordForm: { current: '', next: '', confirm: '' },
            passwordFeedback: '',
            passwordSuccess: false,
            passwordSaving: false,
        }
    },
    methods: {
        async submitPasswordChange() {
            this.passwordFeedback = '';
            this.passwordSuccess = false;

            if (this.passwordForm.next !== this.passwordForm.confirm) {
                this.passwordFeedback = this.$t('common.passwordsDoNotMatch');
                return;
            }

            this.passwordSaving = true;
            try {
                await this.user.changePassword(this.passwordForm.current, this.passwordForm.next);
                this.passwordSuccess = true;
                this.passwordFeedback = this.$t('menu.user_profile.page.passwordChanged');
                this.passwordForm.current = '';
                this.passwordForm.next = '';
                this.passwordForm.confirm = '';
            } catch (err) {
                this.passwordSuccess = false;
                this.passwordFeedback = err.message || this.$t('menu.user_profile.page.passwordChangeError');
            } finally {
                this.passwordSaving = false;
            }
        }
    }
}
</script>

<style scoped>
.profile-page {
}

.page-header {
    margin-bottom: 1.5rem;
}
.page-subtitle {
    color: #9aa0a8;
    font-size: 0.875rem;
    margin-top: 0.125rem;
}

.profile-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    animation: fadeIn 0.4s ease-out;
}
@media (max-width: 768px) {
    .profile-grid {
        grid-template-columns: 1fr;
    }
}

.profile-card {
    padding: 1.75rem;
}
.profile-card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.profile-avatar {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(117, 35, 35, 0.12);
    border: 1px solid rgba(117, 35, 35, 0.2);
    color: #a63c3c;
    font-family: 'IBM Plex Sans', system-ui, sans-serif;
    font-weight: 700;
    font-size: 1.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.profile-identity {
    min-width: 0;
}
.profile-name {
    font-weight: 600;
    font-size: 1.0625rem;
    color: #e8eaed;
}
.profile-email {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.8125rem;
    color: #9aa0a8;
    margin-top: 0.125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.profile-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.profile-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}
.field-label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #5c6370;
}
.field-value {
    font-size: 0.9375rem;
    color: #e8eaed;
}

.markets-card {
    padding: 1.75rem;
}
.markets-card h3 {
    margin-bottom: 1rem;
}
.markets-list {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}
.market-item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-size: 0.875rem;
    color: #e8eaed;
}
.market-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #a63c3c;
    flex-shrink: 0;
}

.password-card {
    padding: 1.75rem;
    grid-column: 1 / -1;
}
.password-card h3 {
    margin-bottom: 1rem;
}
.password-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 360px;
}
.password-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}
.password-field label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #5c6370;
}
.password-field input {
    padding: 0.5rem 0.75rem;
    background: #1a2028;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 6px;
    color: #e8eaed;
    font-size: 0.9375rem;
}
.password-feedback {
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
}
.password-feedback.ok {
    background: rgba(74,222,128,0.1);
    color: #4ade80;
    border: 1px solid rgba(74,222,128,0.3);
}
.password-feedback.err {
    background: rgba(248,113,113,0.1);
    color: #f87171;
    border: 1px solid rgba(248,113,113,0.3);
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
</style>
